// ProcessCard — one background batch process (useBatchAction / useUploadProcess) as a card:
// summary bar, progress, expandable items, retry / fix. Rendered by ActionMonitor ("Zadania w tle").

import React from "react";
import { Button, Collapse, Progress } from "antd";
import {
  CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined,
  ExclamationCircleOutlined, LoadingOutlined, MinusCircleOutlined,
  ReloadOutlined, ToolOutlined,
} from "@ant-design/icons";
import { useProcesses, type BatchItem, type Process } from "@ds/actions/useProcesses";
import { UPLOAD_PROCESS_TYPE } from "@ds/actions/useUploadProcess";
import { SEMANTIC_COLORS, LAYOUT_TOKENS } from "@ds/tokens/tokens";
import { Box } from "@ds/layout/Box";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";

// ── Status helpers ──────────────────────────────────────────────────────

const STATUS_ICON: Record<string, React.ReactNode> = {
  pending: <LoadingOutlined style={{ color: SEMANTIC_COLORS.neutral, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
  processing: <LoadingOutlined spin style={{ color: SEMANTIC_COLORS.info, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
  success: <CheckCircleOutlined style={{ color: SEMANTIC_COLORS.valid, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
  warning: <ExclamationCircleOutlined style={{ color: SEMANTIC_COLORS.amber, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
  error: <CloseCircleOutlined style={{ color: SEMANTIC_COLORS.danger, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
  skipped: <MinusCircleOutlined style={{ color: SEMANTIC_COLORS.neutral, fontSize: LAYOUT_TOKENS.fontSizeBody }} />,
};

// ── Summary bar ─────────────────────────────────────────────────────────

const SummaryBar: React.FC<{ items: BatchItem[] }> = ({ items }) => {
  const counts = { success: 0, warning: 0, error: 0, skipped: 0 };
  for (const item of items) {
    if (item.status in counts) counts[item.status as keyof typeof counts]++;
  }
  const done = counts.success + counts.warning + counts.error + counts.skipped;
  const percent = items.length > 0 ? Math.round((done / items.length) * 100) : 0;

  // Plain "done/total" next to the bar; the bar colour carries warning/error state
  // and the collapsible section below ("Pliki" / "Szczegóły") lists the items.
  return (
    <Inline gap="sm" align="center">
      <Box grow>
        <Progress
          percent={percent}
          size="small"
          showInfo={false}
          strokeColor={counts.error > 0 ? SEMANTIC_COLORS.danger : counts.warning > 0 ? SEMANTIC_COLORS.amber : SEMANTIC_COLORS.valid}
        />
      </Box>
      <TextValue tone="muted" size="sm" tabular nowrap testId="process-card-progress">
        {done}/{items.length}
      </TextValue>
    </Inline>
  );
};

// ── Item row ────────────────────────────────────────────────────────────

const ItemRow: React.FC<{
  item: BatchItem;
  processId: string;
}> = ({ item, processId }) => {
  const { getCallbacks } = useProcesses();
  const cbs = getCallbacks(processId, item.id);

  return (
    <Inline gap="xs" align="start" testId="process-card-item">
      <Box shrink>{STATUS_ICON[item.status] ?? null}</Box>
      <Box grow>
        <TextValue size="xs" wrapWords lineHeight="normal">
          <span>{item.label}</span>
          {item.message && (
            <TextValue size="xs" tone="muted"> — {item.message}</TextValue>
          )}
        </TextValue>
      </Box>
      {item.status === "warning" && cbs?.fix && (
        <Button size="small" type="link" icon={<ToolOutlined />} style={{ padding: 0, height: "auto", fontSize: LAYOUT_TOKENS.fontSizeSmall, flexShrink: 0 }}
          onClick={cbs.fix}>
          Napraw
        </Button>
      )}
      {item.status === "error" && cbs?.retry && (
        <Button size="small" type="link" icon={<ReloadOutlined />} style={{ padding: 0, height: "auto", fontSize: LAYOUT_TOKENS.fontSizeSmall, flexShrink: 0 }}
          data-testid="process-card-item-retry" onClick={cbs.retry}>
          Ponów
        </Button>
      )}
    </Inline>
  );
};

// ── Process card ────────────────────────────────────────────────────────

export const ProcessCard: React.FC<{
  process: Process;
  onRemove: (id: string) => void;
}> = ({ process, onRemove }) => {
  const { getCallbacks } = useProcesses();
  const problemItems = process.items.filter(i => i.status === "error" || i.status === "warning");
  const errorItems = process.items.filter(i => i.status === "error");
  const errorCount = errorItems.length;
  // Batch actions (useBatchAction) register retry callbacks; on an upload card only
  // a failed scan job that filed nothing does (ScanJobWatcher) — otherwise
  // "Ponów błędy" would be a dead button there.
  const canRetry = errorItems.some(i => getCallbacks(process.id, i.id)?.retry);
  const isStale = process.status === "stale";
  // Upload cards — and cards that ask for it (`metadata.listAllItems`, e.g. a scan
  // re-run followed per file) — list every file (name + status; failures carry their
  // error text). Other batch cards keep listing only the items that need attention.
  const isUpload = process.type === UPLOAD_PROCESS_TYPE;
  const listsAll = isUpload || process.metadata?.listAllItems === true;
  const listedItems = listsAll ? process.items : problemItems;
  const listLabel = listsAll ? "Pliki" : "Szczegóły";

  const retryAllErrors = async () => {
    for (const item of errorItems) {
      const cbs = getCallbacks(process.id, item.id);
      if (cbs?.retry) await cbs.retry();
    }
  };

  const headerRight = process.status !== "running" ? (
    <Button type="text" size="small" icon={<DeleteOutlined />}
      onClick={(e) => { e.stopPropagation(); onRemove(process.id); }} />
  ) : null;

  const title = (
    <Inline gap="sm" align="center">
      {process.status === "running" && <LoadingOutlined spin style={{ color: SEMANTIC_COLORS.info }} />}
      {process.status === "completed" && <CheckCircleOutlined style={{ color: SEMANTIC_COLORS.valid }} />}
      {process.status === "completed_with_errors" && <ExclamationCircleOutlined style={{ color: SEMANTIC_COLORS.amber }} />}
      {isStale && <CloseCircleOutlined style={{ color: SEMANTIC_COLORS.neutral }} />}
      <Box grow>
        <Stack gap="xs">
          <TextValue as="div" weight="medium" size="sm" tone={isStale ? "faint" : undefined}>{process.label}</TextValue>
          {/* Reference (e.g. the transaction IRM) on its own, larger line — it is
              what tells one batch from another in the list. */}
          {process.description && (
            <TextValue as="div" size="md" tone={isStale ? "faint" : undefined} testId="process-card-description">
              {process.description}
            </TextValue>
          )}
        </Stack>
      </Box>
      {headerRight}
    </Inline>
  );

  return (
    <Box size="md" border shape="rounded">
      {/* Stale state: dim leaf text via tone="faint" (closed enum) — no opacity. */}
      <Stack gap="sm">
        <div>
          {title}
        </div>
        <SummaryBar items={process.items} />

        {isStale && (
          <TextValue as="div" tone="muted" size="xs">
            Przerwano przez odświeżenie strony. Wróć do źródłowej zakładki aby ponowić.
          </TextValue>
        )}

        {listedItems.length > 0 && !isStale && (
          <Collapse
            ghost
            size="small"
            items={[{
              key: "details",
              label: (
                <TextValue tone="muted" size="xs" testId="process-card-items-toggle">
                  {listLabel} ({listedItems.length})
                </TextValue>
              ),
              children: (
                <div>
                  {listedItems.map(item => (
                    <ItemRow key={item.id} item={item} processId={process.id} />
                  ))}
                </div>
              ),
            }]}
          />
        )}

        {errorCount > 0 && canRetry && !isStale && (
          <Box align="center">
            <Button size="small" icon={<ReloadOutlined />} onClick={retryAllErrors}>
              Ponów błędy ({errorCount})
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
